import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import cn from '../../../../utils/cn';
import { GetCurrencySymbol } from '../../../../utils/Currency';
import FocusControl from '../../../../utils/FocusControl';
import InputField, {
    InputCheckbox,
    InputDateField,
    SelectOption,
    addOption
} from '../../../../utils/InputField';
import { GetFloatNumber } from '../../../../utils/NumberFormatter';
import { mkRelativeUrl } from '../../../../utils/URL';
import { calcVat } from '../../../../utils/VatCalculator';
import { ApiGetActiveProductListOpe } from '../../../Settings/ProductList/ProductOperation';
import { handleNewAddSubscriptionOpe } from '../../../Subscriptions/SubscriptionsOperation';
import { ApiGetPricingServiceList, handleLoadPricing } from '../CustomerDetailsOperation';
import AppServiceList from './Comp/AppServiceList';
import PackageInfo from './Comp/PackageInfo';

const billingCycles = [
    { key: 1, value: 'monthly', text: 'Monthly' },
    { key: 3, value: 'quarterly', text: 'Quarterly' },
    { key: 6, value: 'half-yearly', text: 'Half-Yearly' },
    { key: 12, value: 'yearly', text: 'Yearly' }
];

function AddNewSubscriptions({ data, closeModal }) {
    const navigate = useNavigate();
    const { setMessage } = useTemplate();
    const [isError, setIsError] = useState('');
    const [loading, setLoading] = useState(0); // 1 = submit loading, 2 = pricing loading, 3 = service loading
    const [applicationList, setApplicationList] = useState([]); // Initial Application List
    const [applicationPackageList, setApplicationPackageList] = useState([]); // Application Package List
    const [appServicesList, setAppServicesList] = useState([]); // Initial Service List

    const [formData, setFormData] = useState({
        // ...iniState,
        UserId: data?.id,
        WarrantyStartDate: dayjs().format('YYYY-MM-DD'),
        ServiceRenewPercent: 0,
        ApplicationId: 0,
        SubscriptionName: '',
        DomainName: '',
        PackageId: 0,
        PriceId: 0,
        Status: 'active',
        ServicePackageData: [],
        WarrantyDuration: 0,
        BillingCycle: 0,
        PackagePrice: 0,
        IsQuotation: false,
        VatEnabled: false,
        VatType: 'excluding',
        VatPercent: process.env.REACT_APP_VAT_PERCENT || '7'
    });

    // Set Form Data on change
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name) {
            setFormData((d) => {
                if (name === 'ServiceRenewPercent') {
                    // check regex number only with 2 decimal point and min 0 max 100
                    if (
                        !/^(100(\.0{1,2})?|[0-9]{1,2}(\.[0-9]{1,2})?)$/.test(value) &&
                        value !== ''
                    ) {
                        return d;
                    }
                    return { ...d, ServiceRenewPercent: value };
                }
                return { ...d, [name]: value };
            });
        }
    };

    // Set Application ID
    const handelSetApplicationId = (e) => {
        const { value } = e.target;

        const subscriptionName = applicationList.find(
            (item) => item?.id?.toString() === value
        )?.applicationTitle;

        setAppServicesList([]);

        setFormData((d) => ({
            ...d,
            ApplicationId: value,
            PackageId: 0,
            PriceId: 0,
            PackagePrice: 0,
            ServiceRenewPercent: 0,
            ServicePackageData: [],
            SubscriptionName: value ? `${subscriptionName} - ${data?.organization}` : ''
        }));
    };
    // Set Package Id
    const handelSetApplicationPackageId = (item) => {
        setFormData((d) => ({
            ...d,
            PackageId: item?.packageId,
            PriceId: item?.priceId,
            PackagePrice: item?.amount,
            ServiceRenewPercent: 0,
            ServicePackageData: []
        }));
    };

    // add new subscription handler
    const handleAddSubscription = (e) => {
        setLoading(1);
        e.preventDefault();
        const vatPercent = formData.VatEnabled ? parseFloat(formData.VatPercent) || 0 : 0;
        const vatType = formData.VatEnabled ? formData.VatType : 'excluding';
        const payload = {
            ...formData,
            DefaultVatEnabled: formData.VatEnabled,
            DefaultVatType: vatType,
            DefaultVatPercent: vatPercent
        };
        handleNewAddSubscriptionOpe(payload, setIsError)
            .then(({ data: resData }) => {
                navigate(mkRelativeUrl(`/billing/invoices/${resData?.invoiceId}/info`));
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error.toString(0) });
            })
            .finally(() => {
                setLoading(0);
            });
    };

    // Calculate after percent amount
    const afterPercentAmount = useMemo(() => {
        const percent = parseFloat(formData?.ServiceRenewPercent || 0);
        const packagePrice = parseFloat(formData?.PackagePrice || 0);
        const amount = (packagePrice * percent) / 100;
        return `${amount.toFixed(2)} ${data?.currency || ''}`;
    }, [
        formData?.ServiceRenewPercent,
        formData?.PackagePrice,
        data?.currency,
        formData?.ApplicationId
    ]);

    // Order summary preview: package + addons (free -102 / unfilled -101 count as 0)
    const packagePrice =
        parseFloat(formData?.PackagePrice) > 0 ? parseFloat(formData.PackagePrice) : 0;
    const addonTotal = formData.ServicePackageData.reduce(
        (acc, item) => acc + (item.Amount > 0 ? item.Amount : 0),
        0
    );
    const baseTotal = packagePrice + addonTotal;
    const vatTotals = calcVat(
        baseTotal,
        formData.VatEnabled ? formData.VatPercent : 0,
        formData.VatType
    );

    // --Load Application List [Load when change application ID]
    useEffect(() => {
        setFormData((d) => ({
            ...d,
            ApplicationId: 0,
            PackageId: 0,
            PriceId: 0,
            PackagePrice: 0,
            ServiceRenewPercent: 0,
            ServicePackageData: []
        }));
        ApiGetActiveProductListOpe()
            .then(({ data: resData }) => {
                setApplicationList(resData.applicationList);
            })
            .catch((error) => {
                setMessage({ type: 'error', text: error?.message });
            });
    }, [setMessage]);

    // Load Application Package & Pricing form API
    useEffect(() => {
        if (formData?.ApplicationId) {
            setLoading(2);
            handleLoadPricing(data?.id, formData?.ApplicationId)
                .then(({ data: resData }) => {
                    setApplicationPackageList(resData.packagesList);
                })
                .catch((error) => {
                    setMessage({ type: 'error', text: error.toString(0) });
                })
                .finally(() => setLoading(0));
        }

        // if no application id selected
        if (!formData?.ApplicationId) {
            setApplicationPackageList([]);
            setFormData((d) => ({
                ...d,
                PackageId: 0,
                PriceId: 0,
                ServicePackageData: [],
                PackagePrice: 0,
                ServiceRenewPercent: 0
            }));
        }
    }, [setMessage, formData?.ApplicationId, data?.id]);

    // Load services list
    useEffect(() => {
        // Fetch services list
        if (formData.UserId && formData.ApplicationId > 0 && formData.PackageId) {
            setLoading(3);
            setAppServicesList([]);
            ApiGetPricingServiceList(formData.UserId, formData.ApplicationId, formData.PackageId)
                .then(({ data: resData }) => {
                    setAppServicesList(resData.services);
                    setLoading(0);
                })
                .catch((err) => {
                    setLoading(0);
                    setMessage({ type: 'error', text: err.toString() });
                });
        } else {
            setAppServicesList([]);
        }
    }, [setMessage, formData.UserId, formData.ApplicationId, formData.PackageId]);

    // Focus Control
    FocusControl(isError, setIsError, formData);

    return (
        <form className="flex flex-col gap-4 md:h-full" onSubmit={handleAddSubscription}>
            <div className="flex flex-col md:flex-row gap-6 flex-1 min-h-0">
                <div className="flex flex-col gap-2 flex-1 md:overflow-y-auto md:min-h-0 custom-scrollbar">
                    <SelectOption
                        label="Application"
                        name="ApplicationId"
                        formData={formData}
                        onChange={handelSetApplicationId}
                        options={applicationList?.map((item) =>
                            addOption(item?.id, item?.applicationTitle, item?.id)
                        )}
                        required
                        disabled={applicationList?.length === 0}
                    />
                    <InputField
                        label="Subscription Name"
                        placeholder="EX: Basic"
                        name="SubscriptionName"
                        onChange={handleChange}
                        formData={formData}
                        required
                    />
                    {/* Warranty Start Date */}
                    <div>
                        <InputDateField
                            label="Warranty Start Date"
                            name="WarrantyStartDate"
                            formData={formData}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    {/* Warranty Duration */}
                    <InputField
                        label="Warranty Duration (months)"
                        placeholder="EX: Enter your number in month"
                        name="WarrantyDuration"
                        onChange={handleChange}
                        formData={formData}
                        type="number"
                        min={0}
                        required
                    />

                    {/* Title */}
                    <div className="flex flex-col xxs:flex-row gap-2 w-full">
                        <div className="flex flex-col gap-1 flex-1">
                            <h1>
                                Service Agreement <span className="text-error">*</span>
                            </h1>
                            {/* <span>% of total value of products.</span> */}
                            <div className="flex justify-between pl-0 px-3 items-center gap-2 bg-primary-50 rounded-5 border overflow-hidden h-8.5 md:h-10">
                                <input
                                    type="text"
                                    className={cn(
                                        'w-20 pl-2 p-1 border-gray-300 focus:outline-none inline-block h-full',
                                        {
                                            'bg-primary-50': formData?.PackageId === 0
                                        }
                                    )}
                                    value={
                                        formData?.PackageId === 0
                                            ? 0
                                            : formData?.ServiceRenewPercent
                                    }
                                    onChange={handleChange}
                                    name="ServiceRenewPercent"
                                    disabled={
                                        formData?.ApplicationId === 0 || formData?.PackageId === 0
                                    }
                                    min={0}
                                />
                                <span>% of Year</span>
                            </div>
                            {/* Service Renew Price */}
                            <div className="text-sm text-secondary ">
                                Service Renew Price:&nbsp;
                                <span className="font-medium">{afterPercentAmount}</span>
                            </div>
                        </div>
                        <SelectOption
                            label="Billing Cycle"
                            name="BillingCycle"
                            formData={formData}
                            onChange={handleChange}
                            className="flex-1"
                            options={billingCycles.map((item) =>
                                addOption(item.value, item.text, item.key)
                            )}
                            required
                            // required if there is no service selected then disable inline condition
                            disabled={formData?.ApplicationId === 0 || formData?.PackageId === 0}
                        />
                    </div>
                    {/* Show total  */}
                    <InputField
                        label="Domain"
                        placeholder='EX: "www.example.com"'
                        name="DomainName"
                        onChange={handleChange}
                        formData={formData}
                        optional
                    />
                    <InputCheckbox
                        label="Bill To Agency"
                        name="IsQuotation"
                        formData={formData}
                        onChange={(e) =>
                            setFormData((prev) => ({ ...prev, IsQuotation: e.target.checked }))
                        }
                        text="Create as Quotation"
                        className="mt-1 " // [&>div>span]:text-warning"
                    />
                    <div className="text-sm text-error mt-2">
                        * If you check &quot;Create as Quotation&quot;, the invoice will be created
                        as a quotation and you can convert it to an invoice after approval.
                    </div>

                    {/* VAT Configuration */}
                    <div className="flex flex-col gap-1 border-t pt-2 mt-1">
                        <span className="text-sm font-medium">VAT Configuration</span>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm"
                                checked={formData.VatEnabled}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        VatEnabled: e.target.checked
                                    }))
                                }
                            />
                            <span>Enable VAT</span>
                        </label>
                        {formData.VatEnabled && (
                            <div className="flex flex-col gap-2 ml-1 pl-3 border-l-2 border-base-300">
                                <div className="flex flex-col gap-1">
                                    <span className="text-xs font-medium text-gray-600">
                                        VAT Type
                                    </span>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="VatType"
                                            value="excluding"
                                            checked={formData.VatType === 'excluding'}
                                            onChange={handleChange}
                                            className="radio radio-xs"
                                        />
                                        <span>Excluding VAT</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                                        <input
                                            type="radio"
                                            name="VatType"
                                            value="including"
                                            checked={formData.VatType === 'including'}
                                            onChange={handleChange}
                                            className="radio radio-xs"
                                        />
                                        <span>Including VAT</span>
                                    </label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-gray-600">
                                        VAT Percent
                                    </label>
                                    <input
                                        type="number"
                                        name="VatPercent"
                                        value={formData.VatPercent}
                                        onChange={handleChange}
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        className="w-20 h-6 px-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                                    />
                                    <span className="text-xs text-gray-500">%</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex flex-col md:flex-1 md:min-h-0">
                        <PackageInfo
                            applicationPackageList={applicationPackageList}
                            handelSetApplicationPackageId={handelSetApplicationPackageId}
                            formData={formData}
                            isLoading={loading === 2}
                            isSelectedApplication={formData?.ApplicationId > 0}
                        />

                        <AppServiceList
                            isLoading={loading === 3}
                            appServicesList={appServicesList}
                            formData={formData}
                            setFormData={setFormData}
                        />
                    </div>

                    {/* Order summary */}
                    <div className="border-t pt-1 mt-2">
                        <table className="w-full text-xs">
                            <tbody className="divide-y">
                                <tr className="flex justify-between items-center py-1">
                                    <td className="text-xs font-medium">Package Price</td>
                                    <td className="text-right ml-1 text-xs">
                                        {GetCurrencySymbol(data?.currency)}
                                        {GetFloatNumber(packagePrice)}
                                    </td>
                                </tr>
                                <tr className="flex justify-between items-center py-1">
                                    <td className="text-xs font-medium">Addon Services</td>
                                    <td className="text-right ml-1 text-xs">
                                        {GetCurrencySymbol(data?.currency)}
                                        {GetFloatNumber(addonTotal)}
                                    </td>
                                </tr>
                                {formData.VatEnabled ? (
                                    <>
                                        <tr className="flex justify-between items-center py-1">
                                            <td className="text-xs font-medium">Sub Total</td>
                                            <td className="text-right ml-1 text-xs">
                                                {GetCurrencySymbol(data?.currency)}
                                                {GetFloatNumber(vatTotals.netAmount)}
                                            </td>
                                        </tr>
                                        <tr className="flex justify-between items-center py-1">
                                            <td className="text-xs font-medium">
                                                VAT ({formData.VatPercent}%)
                                            </td>
                                            <td className="text-right ml-1 text-xs">
                                                {GetCurrencySymbol(data?.currency)}
                                                {GetFloatNumber(vatTotals.vatAmount)}
                                            </td>
                                        </tr>
                                        <tr className="flex justify-between items-center py-1 font-bold border-t-2 border-gray-300">
                                            <td className="text-xs">Grand Total</td>
                                            <td className="text-right ml-1 text-xs">
                                                {GetCurrencySymbol(data?.currency)}
                                                {GetFloatNumber(vatTotals.grossAmount)}
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr className="flex justify-between items-center py-1 font-bold border-t-2 border-gray-300">
                                        <td className="text-xs">Total</td>
                                        <td className="text-right ml-1 text-xs">
                                            {GetCurrencySymbol(data?.currency)}
                                            {GetFloatNumber(baseTotal)}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="w-full flex justify-end items-center gap-2 mb-2">
                <ButtonCancel title="Cancel" onClick={() => closeModal(false)} />
                <ButtonPrimary
                    title="Create Invoice"
                    btnType="submit"
                    loading={loading === 1}
                    disabled={formData.ServiceRenewPercent === ''}
                />
            </div>
        </form>
    );
}

export default AddNewSubscriptions;
